import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import questionService from '../../services/question.service';

function QuestionList() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await questionService.getQuestions();
      setQuestions(response.data.questions);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      setError('Failed to load questions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (question) => {
    setSelectedQuestion(question);
    setDeleteDialog(true);
  };

  const handleDialogClose = () => {
    setDeleteDialog(false);
    setSelectedQuestion(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedQuestion) return;

    setDeleteLoading(true);

    try {
      await questionService.deleteQuestion(selectedQuestion.id);
      setQuestions(questions.filter((q) => q.id !== selectedQuestion.id));
      handleDialogClose();
    } catch (err) {
      console.error('Failed to delete question:', err);
      setError('Failed to delete question. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Get status chip based on question's active_date and end_date
  const getStatusChip = (question) => {
    const now = new Date();
    const activeDate = new Date(question.active_date);
    const endDate = question.end_date ? new Date(question.end_date) : null;

    if (activeDate > now) {
      return <Chip size='small' label='Scheduled' color='info' />;
    } else if (endDate && endDate < now) {
      return <Chip size='small' label='Expired' color='default' />;
    } else {
      return <Chip size='small' label='Active' color='success' />;
    }
  };

  return (
    <Container maxWidth='lg'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}>
        <Box>
          <Typography variant='h4' component='h1' gutterBottom>
            Questions
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            Manage trivia questions
          </Typography>
        </Box>

        <Button
          component={Link}
          to='/questions/create'
          variant='contained'
          startIcon={<AddIcon />}>
          Add Question
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : questions.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant='body1' color='text.secondary'>
                No questions found. Add your first question to get started.
              </Typography>
              <Button
                component={Link}
                to='/questions/create'
                variant='outlined'
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}>
                Add Question
              </Button>
            </Box>
          ) : (
            <Table>
              <TableHead sx={{ bgcolor: 'background.default' }}>
                <TableRow>
                  <TableCell width='40%'>Question</TableCell>
                  <TableCell>Correct Answer</TableCell>
                  <TableCell>Active Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {questions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((question) => (
                    <TableRow key={question.id}>
                      <TableCell>
                        <Typography
                          variant='body2'
                          noWrap
                          sx={{ maxWidth: 300 }}>
                          {question.question_text}
                        </Typography>
                      </TableCell>
                      <TableCell>{question.correct_answer}</TableCell>
                      <TableCell>
                        {format(new Date(question.active_date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>{getStatusChip(question)}</TableCell>
                      <TableCell align='right'>
                        <IconButton
                          component={Link}
                          to={`/questions/${question.id}`}
                          color='primary'
                          size='small'
                          sx={{ mr: 1 }}>
                          <VisibilityIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          component={Link}
                          to={`/questions/${question.id}`}
                          color='secondary'
                          size='small'
                          sx={{ mr: 1 }}>
                          <EditIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          color='error'
                          size='small'
                          onClick={() => handleDeleteClick(question)}>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {!loading && questions.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={questions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={handleDialogClose}
        aria-labelledby='delete-dialog-title'>
        <DialogTitle id='delete-dialog-title'>Delete Question</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this question? This action cannot be
            undone.
          </DialogContentText>
          {selectedQuestion && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
              }}>
              <Typography variant='body2' fontWeight='bold'>
                "{selectedQuestion.question_text}"
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color='error'
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default QuestionList;
